import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { logDebug } from "@/lib/log";
import { trackGAAffiliateClick } from "@/lib/ga";
import { Shield, CheckCircle2, ExternalLink, Stethoscope, ShoppingBag } from "lucide-react";

/**
 * MonetizationModule — surfaces affiliate + directory content at high-intent moments.
 * Renders 3 stacked sections: insurance, starter kit, vet clinics.
 * All external links open in new tab with rel="noopener sponsored".
 * Every outbound affiliate click is tracked via /api/affiliate/click.
 */

// Fire-and-forget click tracker. Non-blocking so we never delay the outbound nav.
function trackClick(partner, slot, breedId) {
  try {
    trackGAAffiliateClick(partner, slot);
    axios.post(
      `${API}/affiliate/click`,
      { partner, slot, breed_id: breedId || null }
    ).catch((error) => logDebug("Affiliate click ping failed:", error?.message));
  } catch (error) {
    logDebug("Affiliate click suppressed:", error?.message);
  }
}

export default function MonetizationModule({ breedId }) {
  const [ins, setIns] = useState([]);
  const [kit, setKit] = useState({ items: [], estimated_total_aud: 0 });
  const [vets, setVets] = useState([]);

  useEffect(() => {
    axios.get(`${API}/monetization/insurance`).then((r) => setIns(r.data.partners));
    axios.get(`${API}/monetization/starter-kit`).then((r) => setKit(r.data));
    axios.get(`${API}/monetization/vets`).then((r) => setVets(r.data.vets));
  }, [breedId]);

  return (
    <div className="mt-20 space-y-16">
      {/* Insurance */}
      <section>
        <div className="flex items-center gap-3 mb-2">
          <Shield size={20} className="text-primary" />
          <h2 className="font-display font-bold text-2xl md:text-3xl text-accent">Insure your new best mate</h2>
        </div>
        <p className="text-muted-foreground max-w-2xl mb-6">
          Vet bills for a serious accident or illness routinely run $3,000-$15,000 in Australia. Sign up in the first 14 days after adoption for the widest cover — pre-existing conditions get excluded fast.
        </p>
        <div className="grid md:grid-cols-3 gap-5">
          {ins.map((p) => (
            <a
              key={p.name}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer sponsored"
              onClick={() => trackClick(p.name, "insurance", breedId)}
              data-testid={`insurance-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="group rounded-3xl border border-border/60 warm-shadow p-6 bg-card hover:-translate-y-1 transition-transform flex flex-col"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display font-bold text-xl text-accent">{p.name}</h3>
                  <div className="text-xs text-primary font-semibold uppercase tracking-wider mt-1">{p.highlight}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-muted-foreground">From</div>
                  <div className="font-display font-bold text-2xl text-accent leading-none">{p.from_monthly}</div>
                  <div className="text-xs text-muted-foreground">AUD/mo</div>
                </div>
              </div>
              <p className="mt-3 text-sm text-accent/80 leading-relaxed">{p.tagline}</p>
              <ul className="mt-4 space-y-1.5 text-sm text-accent/80">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2">
                    <CheckCircle2 size={14} className="text-primary shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5 pt-4 border-t border-border/60 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                Get an instant quote <ExternalLink size={12} className="group-hover:translate-x-0.5 transition-transform" />
              </div>
            </a>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Fetchright may earn a small commission when you sign up via these links — it never affects the price you pay.
        </p>
      </section>

      {/* Starter kit */}
      <section>
        <div className="flex items-center gap-3 mb-2">
          <ShoppingBag size={20} className="text-primary" />
          <h2 className="font-display font-bold text-2xl md:text-3xl text-accent">First-day starter kit</h2>
        </div>
        <p className="text-muted-foreground max-w-2xl mb-6">
          Everything you need before day one. Total est. cost: <span className="font-semibold text-accent">${kit.estimated_total_aud} AUD</span>. Buy from PetCircle / Petbarn — Fetchright earns a small commission on the sale.
        </p>
        <div className="rounded-3xl border border-border/60 warm-shadow bg-card overflow-hidden">
          {kit.items.map((k, i) => (
            <a
              key={k.category}
              href={k.url}
              target="_blank"
              rel="noopener noreferrer sponsored"
              onClick={() => trackClick(k.url.includes("petcircle") ? "PetCircle" : "Petbarn", "starter-kit", breedId)}
              data-testid={`kit-${i}`}
              className={`flex items-center gap-4 px-6 py-4 group hover:bg-muted/50 transition-colors ${
                i < kit.items.length - 1 ? "border-b border-border/60" : ""
              }`}
            >
              <div className="w-9 h-9 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0">
                <CheckCircle2 size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-accent">{k.item}</div>
                <div className="text-xs text-muted-foreground">{k.category}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-semibold text-accent">≈ ${k.est_cost}</div>
                <div className="text-xs text-primary inline-flex items-center gap-1">
                  Shop <ExternalLink size={10} className="group-hover:translate-x-0.5 transition-transform" />
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* Vets */}
      <section>
        <div className="flex items-center gap-3 mb-2">
          <Stethoscope size={20} className="text-primary" />
          <h2 className="font-display font-bold text-2xl md:text-3xl text-accent">Book a first vet check</h2>
        </div>
        <p className="text-muted-foreground max-w-2xl mb-6">
          A new-pet health check within the first two weeks catches genetic issues early and starts vaccinations on the right schedule.
        </p>
        <div className="grid md:grid-cols-3 gap-5">
          {vets.map((v, i) => (
            <a
              key={v.name}
              href={v.url}
              target="_blank"
              rel="noopener noreferrer sponsored"
              onClick={() => trackClick(v.name, "vet", breedId)}
              data-testid={`vet-${i}`}
              className="group rounded-3xl border border-border/60 warm-shadow p-6 bg-card hover:-translate-y-1 transition-transform"
            >
              <div className="text-xs uppercase tracking-wider text-primary font-semibold">{v.coverage}</div>
              <h3 className="mt-2 font-display font-bold text-xl text-accent">{v.name}</h3>
              <p className="mt-2 text-sm text-accent/80 leading-relaxed">{v.description}</p>
              <div className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                Find a clinic <ExternalLink size={12} className="group-hover:translate-x-0.5 transition-transform" />
              </div>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
